import { ApiClientError, login } from '../api/client.js';
import { navigate, type View } from '../router.js';
import { el } from '../dom.js';
import { authBrand } from './authBrand.js';
import { isNative } from '../platform.js';

/**
 * Where to go after signing in. Only an in-app PATH is honoured: anything that
 * is not a single leading slash (`//evil.example`, `https://...`, `javascript:`)
 * falls back to home, so a crafted link cannot bounce a fresh login off-site.
 */
function afterLogin(next: string | null): string {
  return next !== null && /^\/(?!\/)/.test(next) ? next : '/';
}

export const loginView: View = (outlet, ctx) => {
  const error = el('p', { class: 'form-error', hidden: true });
  const submit = el('button', { type: 'submit', class: 'btn' }, 'Log in');

  const username = el('input', {
    id: 'username',
    name: 'username',
    type: 'text',
    required: true,
    autocomplete: 'username',
    inputmode: 'email',
    placeholder: 'you@example.com or 555 123 4567',
  }) as HTMLInputElement;

  const password = el('input', {
    id: 'password',
    name: 'password',
    type: 'password',
    required: true,
    autocomplete: 'current-password',
  }) as HTMLInputElement;

  // Checked by default: this is a phone-first app, and the expected behaviour is
  // that opening it signs you straight in. Unchecking keeps the session to this
  // tab only, for a shared or public computer.
  const stayLoggedIn = el('input', {
    id: 'stay-logged-in',
    name: 'stayLoggedIn',
    type: 'checkbox',
    checked: true,
  }) as HTMLInputElement;

  // method="post" even though JS submits it. If the script ever fails, the
  // browser must not fall back to GET and leak credentials into the URL.
  // Carried over from the PHP implementation as a hard rule.
  const form = el('form', { method: 'post', action: '/login', novalidate: true }, [
    el('label', { for: 'username' }, 'Email or phone'),
    username,
    el('label', { for: 'password' }, 'Password'),
    password,
    el('label', { class: 'check-row', for: 'stay-logged-in' }, [
      stayLoggedIn,
      el('span', {}, 'Stay logged in'),
    ]),
    error,
    submit,
  ]) as HTMLFormElement;

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    error.hidden = true;
    submit.setAttribute('disabled', 'disabled');
    try {
      await login(username.value, password.value, stayLoggedIn.checked);
      navigate(afterLogin(ctx.query.get('next')), true);
    } catch (err) {
      error.textContent =
        err instanceof ApiClientError ? err.message : 'Something went wrong. Try again.';
      error.hidden = false;
    } finally {
      submit.removeAttribute('disabled');
    }
  });

  outlet.append(
    el('main', { class: 'auth-card' }, [
      // The signed-out screens carry no chrome at all (see BARE_PATHS in
      // chrome.ts), so without this the logo is absent from the one screen every
      // new visitor sees first. An h1 is still here for document structure; the
      // image inside it is what is actually read.
      authBrand('h1'),
      form,
      el('p', {}, [el('a', { href: '/forgot' }, 'Forgot your password?')]),
      el('p', {}, [el('a', { href: '/register' }, 'Create an account')]),
      // Not inside the app itself, where it would offer to download what is
      // already running.
      el('p', { hidden: isNative() }, [el('a', { href: '/app' }, 'Get the iPhone app')]),
      el('p', { class: 'muted small' }, [
        el('a', { href: '/privacy' }, 'Privacy Policy'),
        ' · ',
        el('a', { href: '/terms' }, 'Terms and Conditions'),
      ]),
    ]),
  );
};
